import { useState } from 'react'
import {
  Users, Search, Ban, CheckCircle2, RefreshCw, ShieldCheck, ShieldOff,
  CalendarPlus, ExternalLink, Tag, History, XCircle,
} from 'lucide-react'
import {
  getAdminUsers, getAdminUser, adminSetUserActive, getSubscriptionPlans,
  adminExtendSubscription, adminCancelSubscription, adminActivateSubscription,
  adminVerifyHash, adminConfirmPayment, adminRejectPayment,
} from '../../services/api'
import {
  C, Card, Row, Btn, Chips, Loading, ErrorLine, Empty,
  inputStyle, selectStyle, labelStyle, useAsync,
  fmtDate, fmtDateTime, fmtMoney, userLabel, Pill, divider, confirmAsync,
} from './adminUI'
import { ratesSummary } from '../../utils/fundingThreshold'

// Раздел «Пользователи» — это теперь ещё и «Подписки»: открытая карточка юзера
// даёт всё, что нужно для работы с конкретным человеком: блокировка, подписка
// (продлить/аннулировать/выдать тариф), проверка хэша транзакции в сети и
// история оплат с ручным подтверждением/отклонением заявки.
// Блокировка на бэкенде ещё и останавливает боты юзера — в ответе приходит
// botsStopped, показываем это в подтверждении, чтобы админ видел последствие.

const FILTERS = [
  { key: 'all',        label: 'Все' },
  { key: 'today',      label: 'Сегодня' },
  { key: 'subscribed', label: 'С подпиской' },
  { key: 'nosub',      label: 'Без подписки' },
  { key: 'expired',    label: 'Истекли' },
  { key: 'paid',       label: 'Платили' },
  { key: 'withbots',   label: 'С ботами' },
  { key: 'withkeys',   label: 'С ключами' },
  { key: 'blocked',    label: 'Заблокированы' },
]

const PAGE = 50

// Сети для проверки хэша (ключи — как в БД и verify_tx.py).
const VERIFY_NETWORKS = [
  { key: 'TRC20',    name: 'USDT · TRC-20' },
  { key: 'BEP20',    name: 'USDT · BEP-20' },
  { key: 'SOL_USDT', name: 'USDT · SOL'    },
  { key: 'TON',      name: 'USDT · TON'    },
]

// Статусы заявок на оплату (payment_requests.status).
const PAY_STATUS = {
  pending:   { color: C.warn,   label: 'ждёт' },
  paid:      { color: C.accent, label: 'оплачено' },
  expired:   { color: C.dim,    label: 'истекла' },
  failed:    { color: C.danger, label: 'отклонена' },
  cancelled: { color: C.dim,    label: 'отменена' },
}

// Заголовок смыслового блока внутри карточки юзера.
function BlockTitle({ icon: Icon, color, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#fff', marginBottom: 8 }}>
      <Icon size={14} color={color} />
      <span>{children}</span>
    </div>
  )
}

export default function UsersSection() {
  const [filter, setFilter] = useState('all')
  const [query, setQuery] = useState('')      // поле ввода
  const [q, setQ] = useState('')              // применённый поиск
  const [page, setPage] = useState(0)
  const [openId, setOpenId] = useState(null)
  const [busy, setBusy] = useState('')        // ключ текущей операции (блокирует её кнопку)
  const [note, setNote] = useState('')

  // Детальная карточка открытого юзера: { user, subscription, payments }.
  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState('')

  // Тарифы для выдачи подписки (getSubscriptionPlans — публичный, доступен всегда).
  const plansAsync = useAsync(() => getSubscriptionPlans(), [])
  const plans = plansAsync.data?.plans || []

  // Действия с подпиской
  const [extendDays, setExtendDays] = useState('30')
  // Тип счёта для продления: '' — как сейчас, 'real' — дни реального счёта,
  // 'demo' — дни демо. Нужно, чтобы демо-юзеру можно было выдать реальные дни.
  const [extendKind, setExtendKind] = useState('')
  const [actPlan, setActPlan] = useState('')

  // Проверка хэша транзакции
  const [verifyNet, setVerifyNet] = useState('TRC20')
  const [verifyTx, setVerifyTx] = useState('')
  const [verifyAddr, setVerifyAddr] = useState('')
  const [verifyResult, setVerifyResult] = useState(null)
  const [verifyErr, setVerifyErr] = useState('')

  const { data, loading, error, reload } = useAsync(
    () => getAdminUsers({ q, filter, limit: PAGE, offset: page * PAGE }),
    [q, filter, page],
  )

  const apply = () => { setPage(0); setQ(query.trim()) }
  const isBusy = (k) => busy === k || busy.startsWith(`${k}:`)

  const fetchDetail = async (id) => {
    setDetailLoading(true); setDetailError('')
    try {
      const res = await getAdminUser(id)
      setDetail(res)
      if (res.subscription?.plan) setActPlan(res.subscription.plan)
      else if (plans.length) setActPlan(plans[0].code)
    } catch (e) {
      setDetailError(e?.message || 'Не удалось загрузить карточку')
    } finally {
      setDetailLoading(false)
    }
  }

  const openCard = (id) => {
    if (openId === id) { setOpenId(null); setDetail(null); return }
    setOpenId(id)
    setDetail(null)
    setVerifyResult(null); setVerifyErr(''); setVerifyTx(''); setVerifyAddr('')
    setNote('')
    fetchDetail(id)
  }

  // Перечитываем карточку после операции (продление/отмена/активация/заявка).
  const refresh = async () => {
    if (!openId) return
    try { setDetail(await getAdminUser(openId)) } catch { /* карточку покажет старый detail */ }
  }

  const toggleActive = async (u) => {
    const next = !u.is_active
    if (!next && !(await confirmAsync(`Заблокировать ${userLabel(u)} (id ${u.id})? Боты юзера будут остановлены.`))) return
    setBusy('block'); setNote('')
    try {
      const r = await adminSetUserActive(u.id, next)
      setNote(next
        ? `Юзер ${u.id} разблокирован`
        : `Юзер ${u.id} заблокирован${r?.botsStopped ? `, ботов остановлено: ${r.botsStopped}` : ''}`)
      await refresh()
      reload()
    } catch (e) {
      setNote(`Ошибка: ${e?.message || e}`)
    } finally {
      setBusy('')
    }
  }

  // ── Подписка: продлить / аннулировать / выдать тариф ───────────────────
  const extend = async () => {
    const days = parseInt(extendDays, 10)
    if (!openId || !days || days < 1 || days > 365) { setNote('Дней: 1..365'); return }
    setBusy('extend'); setNote('')
    try {
      const r = await adminExtendSubscription(openId, days, extendKind || undefined)
      setDetail(d => ({ ...d, subscription: r.subscription }))
      const kindText = r.kind === 'real' ? ' · реальный счёт' : r.kind === 'demo' ? ' · демо-счёт' : ''
      setNote(`Подписка продлена до ${fmtDate(r.expiresAt)}${kindText}`)
      reload()
    } catch (e) { setNote(`Ошибка: ${e?.message || e}`) }
    finally { setBusy('') }
  }

  const cancelSub = async () => {
    if (!openId) return
    if (!(await confirmAsync(`Аннулировать подписку юзера ${openId}? Доступ к торговле закроется.`))) return
    setBusy('cancel'); setNote('')
    try {
      const r = await adminCancelSubscription(openId)
      setDetail(d => ({ ...d, subscription: r.subscription }))
      setVerifyResult(null)
      setNote('Подписка аннулирована')
      reload()
    } catch (e) { setNote(`Ошибка: ${e?.message || e}`) }
    finally { setBusy('') }
  }

  const activate = async (opts = {}) => {
    if (!openId || !actPlan) return
    setBusy('activate'); setNote('')
    try {
      const r = await adminActivateSubscription(openId, actPlan, opts)
      setDetail(d => ({ ...d, subscription: r.subscription }))
      setVerifyResult(null)
      setNote(`Тариф ${actPlan} выдан до ${fmtDate(r.expiresAt)}`)
      reload()
    } catch (e) { setNote(`Ошибка: ${e?.message || e}`) }
    finally { setBusy('') }
  }

  // ── Проверка хэша транзакции в сети ────────────────────────────────────
  const verify = async () => {
    const tx = String(verifyTx).trim()
    if (!tx) return
    setBusy('verify'); setVerifyErr(''); setVerifyResult(null); setNote('')
    try {
      setVerifyResult(await adminVerifyHash(verifyNet, tx, String(verifyAddr).trim()))
    } catch (e) { setVerifyErr(e?.message || 'Ошибка проверки хэша') }
    finally { setBusy('') }
  }

  // ── Заявки на оплату: ручное подтверждение / отклонение ────────────────
  const confirmPay = async (pr) => {
    if (!(await confirmAsync(
      `Подтвердить заявку #${pr.id} (${pr.plan_code}, ${fmtMoney(pr.amount_usdt)} USDT)? Заявка станет «оплачено», подписка активируется.`,
    ))) return
    setBusy(`confirm:${pr.id}`); setNote('')
    try {
      await adminConfirmPayment(pr.id, pr.tx_hash || undefined)
      setNote(`Заявка #${pr.id} подтверждена, подписка активирована`)
      await refresh(); reload()
    } catch (e) { setNote(`Ошибка: ${e?.message || e}`) }
    finally { setBusy('') }
  }

  const rejectPay = async (pr) => {
    if (!(await confirmAsync(`Отклонить заявку #${pr.id} (${pr.plan_code})?`))) return
    setBusy(`reject:${pr.id}`); setNote('')
    try {
      await adminRejectPayment(pr.id)
      setNote(`Заявка #${pr.id} отклонена`)
      await refresh(); reload()
    } catch (e) { setNote(`Ошибка: ${e?.message || e}`) }
    finally { setBusy('') }
  }

  const rows = data?.rows || []
  const total = data?.total || 0
  const shownTo = page * PAGE + rows.length

  const planTitle = (code) => {
    if (!code) return '—'
    const p = plans.find(x => x.code === code)
    return p ? `${p.title_ru} · $${fmtMoney(p.price_usdt)}` : code
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') apply() }}
          placeholder="id, @username или имя"
          style={{ ...inputStyle, flex: 1 }}
        />
        <Btn icon={Search} onClick={apply}>Найти</Btn>
      </div>

      <Chips options={FILTERS} value={filter} onChange={v => { setFilter(v); setPage(0) }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1, fontSize: 11.5, color: C.dim }}>
          {loading ? 'Загрузка…' : `Показано ${page * PAGE + (rows.length ? 1 : 0)}–${shownTo} из ${total}`}
        </div>
        <Btn small ghost icon={RefreshCw} color={C.muted} onClick={reload}>Обновить</Btn>
      </div>

      <ErrorLine onRetry={reload}>{error}</ErrorLine>
      {note && (
        <div style={{ fontSize: 12, color: note.startsWith('Ошибка') ? C.danger : C.accent, marginBottom: 10 }}>
          {note}
        </div>
      )}

      {loading && !rows.length ? <Loading /> : !rows.length ? <Empty>Никого не найдено</Empty> : (
        <Card style={{ padding: '6px 14px' }}>
          {rows.map((u, i) => {
            const open = openId === u.id
            const sub = detail?.subscription
            const rates = ratesSummary(detail?.bots)
            return (
              <div key={u.id} style={{ borderTop: i ? `1px solid ${C.line}` : 'none', padding: '10px 0' }}>
                <div
                  onClick={() => openCard(u.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: u.is_active ? '#fff' : C.dim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {userLabel(u)}{!u.is_active && ' · заблокирован'}
                    </div>
                    <div style={{ fontSize: 10.5, color: C.dim, fontFamily: 'monospace', marginTop: 2 }}>
                      id {u.id} · рег. {fmtDate(u.created_at)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                    {u.subActive && <Pill color={C.accent}>подписка</Pill>}
                    {u.botsRunning > 0 && <Pill color={C.indigo}>{u.botsRunning} бот</Pill>}
                    {!u.is_active && <Pill color={C.danger}>блок</Pill>}
                  </div>
                </div>

                {open && (
                  <div style={{ marginTop: 8, background: C.inner, borderRadius: 12, padding: '10px 12px' }}>
                    {detailLoading ? (
                      <Loading text="Карточка…" />
                    ) : detailError ? (
                      <ErrorLine onRetry={() => fetchDetail(u.id)}>{detailError}</ErrorLine>
                    ) : detail ? (
                      <>
                        {/* ── Общая информация ── */}
                        <Row label="Тариф" value={u.plan ? planTitle(u.plan) : (sub?.subscribed ? 'активна без тарифа' : '—')} />
                        <Row label="Подписка до" value={fmtDate(u.expires_at)} valueColor={u.subActive ? C.accent : C.dim} />
                        {sub?.network && (
                          <Row label="Сеть оплаты" value={(VERIFY_NETWORKS.find(n => n.key === sub.network)?.name) || sub.network} />
                        )}
                        {sub?.tx_hash && (
                          <Row label="Tx hash" value={sub.tx_hash} mono valueSub="внутри: сеть/хэш последней активации" />
                        )}
                        <Row label="Оплатил всего" value={`${fmtMoney(u.paidTotal)} USDT`} />
                        <Row label="Боты" value={`${u.bots || 0} (работают ${u.botsRunning || 0})`} />
                        {/* task.txt, 2026-09-29: порог фандинга юзера — рядом с
                            его стартовым депозитом/объёмом/плечом. У юзера с
                            несколькими ботами пороги разные: показываем все и
                            подписываем, чей какой (правится в разделе «Боты»). */}
                        <Row
                          label="Порог фандинга"
                          value={rates.text}
                          valueSub={rates.sub}
                          valueColor={C.indigo}
                          mono
                        />
                        <Row label="API-ключи" value={u.hasKeys ? 'есть' : 'нет'} valueColor={u.hasKeys ? C.accent : C.dim} />
                        <Row label="Язык" value={u.language || 'ru'} />
                        <Row label="Последний вход" value={fmtDateTime(u.last_seen)} />

                        <div style={divider} />

                        {/* ── Подписка: продлить / аннулировать / выдать ── */}
                        <BlockTitle icon={u.subActive ? ShieldCheck : ShieldOff} color={u.subActive ? C.accent : C.dim}>
                          {u.subActive ? 'Подписка активна' : 'Подписки нет'}
                        </BlockTitle>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={labelStyle}>Продлить на дней</div>
                            <input
                              value={extendDays}
                              onChange={e => setExtendDays(e.target.value.replace(/\D/g, ''))}
                              inputMode="numeric"
                              style={{ ...inputStyle, textAlign: 'center' }}
                            />
                            <select
                              value={extendKind}
                              onChange={e => setExtendKind(e.target.value)}
                              style={{ ...selectStyle, marginTop: 6 }}
                              title="Дни какого счёта выдать. У демо-юзера выбор «Реальный счёт» открывает реальную торговлю на выданные дни."
                            >
                              <option value="">как сейчас (не менять тип)</option>
                              <option value="real">реальный счёт</option>
                              <option value="demo">демо-счёт</option>
                            </select>
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={labelStyle}>Выдать тариф</div>
                            <select value={actPlan} onChange={e => setActPlan(e.target.value)} style={selectStyle}>
                              {!plans.length && <option value="">загрузка…</option>}
                              {plans.map(p => (
                                <option key={p.code} value={p.code}>
                                  {p.title_ru} · ${fmtMoney(p.price_usdt)}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <Btn small icon={CalendarPlus} disabled={isBusy('extend')} onClick={extend}>
                            {isBusy('extend') ? '…' : `Продлить (+${extendDays || 'N'} дн.${extendKind === 'real' ? ', реал' : extendKind === 'demo' ? ', демо' : ''})`}
                          </Btn>
                          <Btn small color={C.accent} disabled={isBusy('activate')} onClick={() => activate()}>
                            {isBusy('activate') ? '…' : 'Выдать тариф'}
                          </Btn>
                          {u.subActive && (
                            <Btn small ghost color={C.danger} icon={ShieldOff} disabled={isBusy('cancel')} onClick={cancelSub}>
                              {isBusy('cancel') ? '…' : 'Аннулировать'}
                            </Btn>
                          )}
                        </div>

                        <div style={divider} />

                        {/* ── История оплат ── */}
                        <BlockTitle icon={History} color={C.indigo}>Оплаты</BlockTitle>
                        {!detail.payments?.length ? (
                          <div style={{ fontSize: 12, color: C.dim, padding: '2px 0 6px' }}>Заявок на оплату не было</div>
                        ) : (
                          detail.payments.map(pr => {
                            const st = PAY_STATUS[pr.status] || { color: C.dim, label: pr.status }
                            return (
                              <div key={pr.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderTop: `1px solid ${C.line}` }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <span style={{ color: C.dim, fontFamily: 'monospace', fontSize: 11 }}>#{pr.id}</span>{' '}
                                  <span style={{ color: '#fff', fontWeight: 600, fontSize: 12.5 }}>{pr.plan_code}</span>{' '}
                                  <span style={{ color: C.muted, fontSize: 11.5 }}>{pr.network}</span>
                                  <div style={{ fontSize: 10.5, color: C.dim, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                    {pr.deposit_address}
                                  </div>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                                  <div style={{ fontSize: 12.5, fontWeight: 700, color: '#fff' }}>{fmtMoney(pr.amount_usdt)} USDT</div>
                                  <Pill color={st.color}>{st.label}</Pill>
                                </div>
                                {pr.status === 'pending' && (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
                                    <Btn small icon={CheckCircle2} disabled={isBusy(`confirm:${pr.id}`)} onClick={() => confirmPay(pr)}>
                                      {isBusy(`confirm:${pr.id}`) ? '…' : 'Подтвердить'}
                                    </Btn>
                                    <Btn small ghost color={C.danger} disabled={isBusy(`reject:${pr.id}`)} onClick={() => rejectPay(pr)}>
                                      {isBusy(`reject:${pr.id}`) ? '…' : 'Отклонить'}
                                    </Btn>
                                  </div>
                                )}
                              </div>
                            )
                          })
                        )}

                        <div style={divider} />

                        {/* ── Проверка хэша транзакции ── */}
                        <BlockTitle icon={ExternalLink} color={C.warn}>Проверить хэш транзакции</BlockTitle>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                          <div style={{ width: 120 }}>
                            <div style={labelStyle}>Сеть</div>
                            <select value={verifyNet} onChange={e => setVerifyNet(e.target.value)} style={selectStyle}>
                              {VERIFY_NETWORKS.map(n => <option key={n.key} value={n.key}>{n.name}</option>)}
                            </select>
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={labelStyle}>Tx hash</div>
                            <input
                              value={verifyTx}
                              onChange={e => setVerifyTx(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') verify() }}
                              placeholder="хэш из блокчейн-эксплорера"
                              style={inputStyle}
                            />
                          </div>
                        </div>
                        <div style={{ marginBottom: 8 }}>
                          <div style={labelStyle}>Адрес получателя (необязательно, ускоряет поиск)</div>
                          <input
                            value={verifyAddr}
                            onChange={e => setVerifyAddr(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') verify() }}
                            placeholder="наш депозитный адрес"
                            style={inputStyle}
                          />
                        </div>
                        <Btn small icon={ExternalLink} color={C.warn} disabled={isBusy('verify') || !verifyTx.trim()} onClick={verify}>
                          {isBusy('verify') ? '…' : 'Проверить хэш'}
                        </Btn>

                        {verifyErr && (
                          <div style={{ fontSize: 12, color: C.danger, marginTop: 8 }}>⚠️ {verifyErr}</div>
                        )}
                        {verifyResult && (
                          <div style={{
                            borderRadius: 12, padding: '10px 12px', marginTop: 8, border: '1px solid',
                            background: verifyResult.found ? 'rgba(16,185,129,0.08)' : 'rgba(248,113,113,0.06)',
                            borderColor: verifyResult.found ? 'rgba(16,185,129,0.3)' : 'rgba(248,113,113,0.2)',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, color: verifyResult.found ? C.accent : C.danger, marginBottom: 6 }}>
                              {verifyResult.found ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                              {verifyResult.found ? 'Транзакция найдена' : 'В сети не найдена'}
                            </div>
                            {verifyResult.found && (
                              <div style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <Row label="Сумма" value={`${verifyResult.amount} USDT`} />
                                {verifyResult.from && <Row label="Отправитель" value={verifyResult.from} mono />}
                                {verifyResult.to && <Row label="Получатель" value={verifyResult.to} mono />}
                                {typeof verifyResult.confirmed === 'boolean' && (
                                  <Row label="Подтверждена" value={verifyResult.confirmed ? '✓' : '…'} valueColor={verifyResult.confirmed ? C.accent : C.warn} />
                                )}
                                {verifyResult.note && <div style={{ fontSize: 11, color: C.dim, lineHeight: 1.4 }}>{verifyResult.note}</div>}
                              </div>
                            )}
                            {verifyResult.found && (
                              <Btn small full color={C.accent} disabled={isBusy('activate')} onClick={() => activate({ network: verifyNet, txHash: String(verifyTx).trim() })} style={{ marginTop: 8 }}>
                                {isBusy('activate') ? '…' : 'Платёж получен — активировать тариф'}
                              </Btn>
                            )}
                          </div>
                        )}

                        <div style={divider} />
                        <Btn
                          full
                          small
                          color={u.is_active ? C.danger : C.accent}
                          icon={u.is_active ? Ban : CheckCircle2}
                          disabled={isBusy('block')}
                          onClick={() => toggleActive(u)}
                        >
                          {isBusy('block') ? '…' : u.is_active ? 'Заблокировать' : 'Разблокировать'}
                        </Btn>
                      </>
                    ) : null}
                  </div>
                )}
              </div>
            )
          })}
        </Card>
      )}

      {total > PAGE && (
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <Btn small ghost color={C.muted} disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>← Назад</Btn>
          <div style={{ flex: 1 }} />
          <Btn small ghost color={C.muted} disabled={shownTo >= total} onClick={() => setPage(p => p + 1)}>Вперёд →</Btn>
        </div>
      )}

      <Card title="Итого" icon={Users} style={{ marginTop: 12 }}>
        <Row label="Найдено по фильтру" value={total} />
      </Card>
    </div>
  )
}
